import React, { useEffect, useState } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Button, Box, Typography, Alert, CardContent, Card, Grid2, Tooltip, FormControl, InputLabel, Select, MenuItem, SelectChangeEvent } from '@mui/material';
import { GridActionsCellItem, GridColDef} from '@mui/x-data-grid';
import SearchBox from '../../components/form/SearchBox/SearchBox';
import { DataGridPro } from '@mui/x-data-grid-pro';
import useAuditLog, { isAuditRangeBlocked } from '../../hooks/useAuditLog';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { format } from 'date-fns';
import { AUDIT_SOURCES, dataGridHeaderHeight, dataGridRowHeight, GLOBALSETTINGS, GRIDCOLUMNKEYS, SEARCHBOX } from '../../data/constants';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AuditLogDetails from './AuditLogDetails';
import { AuditLogDto } from '../../types/AuditLog';
import { GLOBALMESSAGES } from '../../constants/messages';
import { getCurrentESTDate } from '../../utils/dateHelper';
import CustomLoadingOverlay from '../../components/ui/CustomLoadingOverlay';
import UsersDropdownList from '../../components/ui/UsersDropdownList';
import { useResizableColumns } from "../../hooks/useResizableColumns";
import { usePersistedColumnVisibility } from "../../hooks/usePersistedColumnVisibility";

const AuditLog: React.FC = () => {
  const [fromDate, setFromDate] = useState<Date | null>(getCurrentESTDate());
  const [toDate, setToDate] = useState<Date | null>(getCurrentESTDate());
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<AuditLogDto | null>(null);
  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
  const [source, setSource] = useState<string>(AUDIT_SOURCES.PORTAL);
  const [searchClearSignal, setSearchClearSignal] = useState(0);

  const { loading, pageState, paginationModel, setHandleGoTrigger, handleOnRowsScrollEnd, apiRef, handleSearchText } = useAuditLog(
    fromDate,
    toDate,
    selectedUsers,
    source);

  const columns: GridColDef[] = [
    { field: 'auditLogId', headerName: 'ID', flex: 0.1, minWidth: 70 },
    { field: 'auditUser', headerName: 'User Name', width: 90 },
    {
      field: 'auditDate', headerName: 'Date Time', flex: 0.1, minWidth: 180,
      valueFormatter: (value) => value ? format(value, GLOBALSETTINGS.DATE_FORMAT_TIME) : ""
    },
    { field: 'eventType', headerName: 'Event Type', flex: 0.3, minWidth: 200 },
    { field: 'primaryRecordId', headerName: 'Primary Record Id', flex: 0.1, minWidth: 100 },
    { field: 'summary', headerName: 'Summary', flex: 0.3, minWidth: 175 },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.1,
      width: 80,
      hideable: false,
      filterable: false,
      sortable: false,
      disableColumnMenu: false,
      renderCell: (params) => (
        <GridActionsCellItem
          icon={<Tooltip title="View details" placement="top"><VisibilityIcon /></Tooltip>}
          label="View"
          onClick={() => handleView(params.row)}
        />
      ),
    }
  ];

  const handleView = (row: AuditLogDto) => {
    setSelectedRow(row);
    setOpen(true);
  };

  const handleGoClick = async () => {
    if (!fromDate || !toDate) {
      setError(GLOBALMESSAGES.BOTH_DATES);
      return;
    }

    if (toDate < fromDate) {
      setError(GLOBALMESSAGES.TO_DATE_GREATER);
      return;
    }

    //* Epic 80054 - a range wider than AUDITLOG_SQLA_MAX_DAYS with no user selected scans a
    //* huge volume of historical rows and hangs or times out the grid. Return before
    //* setHandleGoTrigger so useAuditLog.loadData never fires the request (no spinner, grid
    //* left as-is). Selecting a user scopes the query, which lifts the restriction.
    //* Applies to Portal as well as SQLA - see isAuditRangeBlocked.
    if (isAuditRangeBlocked(fromDate, toDate, selectedUsers)) {
      setError(GLOBALMESSAGES.AUDITLOG_SQLA_RANGE_TOO_WIDE);
      return;
    }

    setError(null);
    setHandleGoTrigger((prev) => prev + 1);
  };

  const handleClose = () => {
    setOpen(false);
  };
  const handleUserChange = (selectedUserIds: number[]) => {
    setSelectedUsers(selectedUserIds ?? []);
  };

  const handleSourceChange = (event: SelectChangeEvent) => {
    setSource(event.target.value);
    setSearchClearSignal((prev) => prev + 1);
  };

  //* Epic 80054 (AC6) - drop the guard as soon as it stops applying: the range is narrowed
  //* or a user is selected. Switching Source no longer clears it, because the guard is no
  //* longer SQLA-only. Deliberately scoped to that one message so the two date validations
  //* keep their own clearing behavior.
  useEffect(() => {
    if (
      error === GLOBALMESSAGES.AUDITLOG_SQLA_RANGE_TOO_WIDE &&
      !isAuditRangeBlocked(fromDate, toDate, selectedUsers)
    ) {
      setError(null);
    }
  }, [error, fromDate, toDate, selectedUsers]);

    const {
      columnState,
      pinnedColumns,
      setPinnedColumns,
      handleColumnResize,
    } = useResizableColumns(columns);

  const { columnVisibilityModel, setColumnVisibilityModel } = usePersistedColumnVisibility(
    GRIDCOLUMNKEYS.AUDIT_LOG
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <section className='mb-3'>
        <Card>
          <CardContent>
            <Typography variant="h5" component="div" className="mb-3 card-title">Audit Log</Typography>
            <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
              <Box width="100%" textAlign="left">
                <Typography variant="h5">Select Date Range</Typography>
              </Box>
              <Grid2 container spacing={2} width="100%" alignItems="center">
                <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                  <DatePicker
                    label="Date From"
                    value={fromDate}
                    onChange={(newValue) => {
                      setFromDate(newValue)
                      if (newValue && toDate && newValue <= toDate) {
                        setError(null);
                      }
                    }}
                    slotProps={{ textField: { size: 'small', fullWidth: true, inputProps: { readOnly: true } } }}
                    maxDate={new Date()}
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                  <DatePicker
                    label="Date To"
                    value={toDate}
                    onChange={(newValue) => {
                      setToDate(newValue)
                      if (newValue && fromDate && newValue >= fromDate) {
                        setError(null);
                      }
                    }}
                    slotProps={{ textField: { size: 'small', fullWidth: true, inputProps: { readOnly: true } } }}
                    maxDate={new Date()}
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                  <FormControl size="small" fullWidth>
                    <InputLabel id="audit-source-label">Source</InputLabel>
                    <Select
                      labelId="audit-source-label"
                      id="audit-source-select"
                      value={source}
                      label="Source"
                      onChange={handleSourceChange}
                    >
                      <MenuItem value={AUDIT_SOURCES.PORTAL}>Portal</MenuItem>
                      <MenuItem value={AUDIT_SOURCES.SQLA}>SQLA</MenuItem>
                    </Select>
                  </FormControl>
                </Grid2>
                {/* Epic 80054 - the Users filter is available for BOTH sources, not just
                    Portal: scoping a SQLA query to specific users is what makes a range
                    wider than AUDITLOG_SQLA_MAX_DAYS affordable, so it has to be reachable. */}
                <Grid2 size={{ xs: 12, sm: 6, md: 2 }}>
                  <UsersDropdownList userId={null} controlHeight={38} onUserChange={handleUserChange} debounceChange={false} mtValue={0} />
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6, md: 1 }} display="flex">
                  <Button id="btnGo" variant="contained" color="primary" onClick={handleGoClick} fullWidth
                     className='filter-btn'
                    sx={{
                      width: { xs: '100%', sm: 'auto' },
                    }}>
                    Go
                  </Button>
                </Grid2>
              </Grid2>
              {error &&
                <Alert
                  severity="error"
                  sx={{
                    mb: "5px",
                    alignItems: 'flex-start',
                    textAlign: 'left',
                    alignSelf: 'flex-start'
                  }}
                >
                  {error}
                </Alert>
              }
              <Box width="100%">
                <SearchBox searchPlaceholder={SEARCHBOX.TEXT_PLACEHOLDER} onSearch={handleSearchText} clearSignal={searchClearSignal} />
              </Box>

              <Box width="100%">
                <div style={{ height: '600px', width: '100%' }}>
                  <DataGridPro
                    apiRef={apiRef}
                    rowHeight={dataGridRowHeight}
                    rows={pageState.data ?? []}
                    {...pageState.data}
                    columns={columnState}
                    rowCount={pageState.total}
                    loading={loading}
                    columnHeaderHeight={dataGridHeaderHeight}
                    slots={{
                      loadingOverlay: CustomLoadingOverlay,
                    }}
                    paginationModel={paginationModel}
                    paginationMode="server"
                    getRowId={(row) => row.auditLogId}
                    hideFooterPagination
                    localeText={{ noRowsLabel: GLOBALMESSAGES.NOROWSTEXT }}
                    onRowsScrollEnd={handleOnRowsScrollEnd}
                    pinnedColumns={pinnedColumns}
                    onPinnedColumnsChange={(newPinned) => setPinnedColumns(newPinned)}
                    onColumnWidthChange={(params) => handleColumnResize(params.width, params.colDef)}
                    columnVisibilityModel={columnVisibilityModel}
                    onColumnVisibilityModelChange={setColumnVisibilityModel}
                  />
                </div>
              </Box>
              <AuditLogDetails open={open} handleClose={handleClose} selectedRow={selectedRow} source={source} />
            </Box>
          </CardContent>
        </Card>
      </section>
    </LocalizationProvider>
  );
};

export default AuditLog;
